import { Inject, Injectable, Optional } from '@angular/core';
import { Observable }                    from 'rxjs';
import { CS_CHAT_CONFIG, CsChatConfig, CsChatMessage } from '@cs/components/chat';

/**
 * One SSE event from the Cohelion.Mcp.Server chat endpoint. Mirrors the server's polymorphic
 * `ChatEvent` discriminator (Models/Chat/ChatEvent.cs) — kept in sync with the standalone chat
 * app's `ChatStreamEvent` so the shared render logic is identical on both hosts.
 */
export interface PmChatStreamEvent {
	type: 'session.started' | 'session.title' | 'message.started' | 'text.delta' | 'tool.start' | 'tool.end' | 'message.done' | 'artifact.created' | 'todos.updated' | 'code.start' | 'code.output' | 'code.end' | 'context.window' | 'error';
	value?: string;
	tool?: string;
	durationMs?: number;
	timeToFirstMs?: number;
	outputTokens?: number;
	toolsMs?: number;
	input?: unknown;
	result?: string;
	summary?: string;
	model?: string;
	provider?: string;
	startUtc?: string;
	title?: string;
	hasGeneratedTitle?: boolean;
	id?: number;
	name?: string;
	mimeType?: string;
	sizeBytes?: number;
	items?: { text: string; done: boolean }[];
	workspaceId?: string;
	stream?: string;
	/** code.start — the Python source about to run (drives the terminal block's Code section). */
	code?: string;
	/** code.end — the failure reason when ok === false; folded into the terminal as its last output. */
	error?: string;
	text?: string;
	ok?: boolean;
	contextWindow?: number;
	usedTokens?: number;
	cacheReadTokens?: number;
	components?: { key: string; label: string; tokens: number }[];
}

/** Context-window breakdown (context.window) — feeds the composer meter. */
export interface PmContextUsage {
	contextWindow: number;
	usedTokens: number;
	cacheReadTokens: number;
	components: { key: string; label: string; tokens: number }[];
}

/** Full-prompt breakdown for the advanced debug panel (GET /api/chat/debug/context). Shape matches
 *  the generic chat component's CsDebugContext so it can be bound straight through. */
export interface PmDebugContext {
	systemPrompt: string;
	systemPromptTokens: number;
	tools: { name: string; description: string; parameters: string; tokens: number }[];
	toolsTokens: number;
	config: { contextWindowTokens: number; conversationModel: string; analysisModel: string; maxTokens: number };
}

/** A row of the session list (GET /api/chat/sessions). */
export interface PmChatSessionSummary {
	startUtc: string;
	title: string;
	hasGeneratedTitle: boolean;
}

/** A persisted turn (GET /api/chat/sessions/{startUtc}). */
export interface PmChatTurnRecord {
	promptUtc: string;
	responseTimeMs: number;
	prompt: string;
	response: string;
	responseIsCompaction: boolean;
}

/** Thrown on HTTP 401 — the ColdFusion session expired and the user must re-authenticate. */
export class PmChatAuthError extends Error {
	constructor() { super('Authentication required'); this.name = 'PmChatAuthError'; }
}

/**
 * Strips the UI-only metadata fences (```tool / ```stats / ```artifact / ```todos) from assistant
 * content before it is replayed as conversation history — they are client render markers, not model
 * output, and echoing them back makes the model imitate them. Chart fences are genuine model content
 * and are kept. Mirrors `stripUiFences` in the standalone chat app.
 */
export function stripUiFences(content: string): string {
	if (!content) { return ''; }
	return content
		.replace(/```(?:tool|stats|artifact|todos|error)[ \t]*\r?\n[\s\S]*?\r?\n```[ \t]*/g, '')
		// Drop the terminal fence's `code=<base64>` tag. The terminal OUTPUT stays — tool calls are not
		// in the conversation history, so it is the model's only memory of what its own code printed —
		// but the source is a UI affordance, and replaying a base64 blob every turn would burn tokens
		// for nothing (and invite the model to imitate the tag).
		.replace(/(```terminal[^\r\n]*?)[ \t]code=[A-Za-z0-9+/=]+/g, '$1')
		.replace(/\n{3,}/g, '\n\n')
		.trim();
}

/**
 * PM-app transport for the shared <cs-chat> shell. The browser talks to the MCP chat server
 * DIRECTLY (`apiUrl`, e.g. a same-origin `/ai/api/chat` reverse-proxy path) so SSE streams natively —
 * ColdFusion is never in the streaming path. Auth is a short-lived, client-bound JWT: the user is
 * already logged in to ColdFusion (JSESSIONID), so we first POST to `/_api/rest/users/authenticate/ai`
 * (same-origin, cookie-authenticated) to mint a JWT signed with the key MCP shares, then send it as
 * `Authorization: Bearer` on every MCP call. The token is short-lived and bound to this client's
 * fingerprint; we cache it and silently re-mint on expiry or a 401. The one PM-specific request field
 * over the standalone service is `context`: the live page/filter snapshot from {@link AgentContextService}.
 */
@Injectable({ providedIn: 'root' })
export class PmChatService {
	private readonly apiUrl: string;

	/** ColdFusion endpoint that mints the AI JWT for the current login (same-origin, cookie-auth). */
	private readonly aiAuthUrl: string;

	/** Cached AI JWT + its expiry (epoch ms); re-minted lazily. */
	private token: string | null = null;
	private tokenExpiresAt = 0;
	private tokenInFlight: Promise<string> | null = null;

	constructor(@Optional() @Inject(CS_CHAT_CONFIG) config: CsChatConfig) {
		this.apiUrl    = (config && config.apiUrl) || '/ai/api/chat';
		this.aiAuthUrl = '/_api/rest/users/authenticate/ai';
	}

	/**
	 * Full URL to download an artifact by id (against the MCP base). NOTE: MCP authorizes via the
	 * Bearer token, so a plain link/GET won't carry it — the token is appended as `access_token` for
	 * MCP to read from the query on the artifact endpoint (a browser download can't set headers).
	 */
	artifactUrl(id: number): string {
		const q = this.token ? `?access_token=${encodeURIComponent(this.token)}` : '';
		return `${this.apiUrl}/artifacts/${id}${q}`;
	}

	/**
	 * Returns a valid AI JWT, minting one via ColdFusion when the cache is empty/near-expiry. Concurrent
	 * callers share one in-flight mint. `force` bypasses the cache (used after a 401).
	 */
	private async ensureToken(force = false): Promise<string> {
		const now = Date.now();
		if (!force && this.token && now < this.tokenExpiresAt - 30_000) { return this.token; }
		if (this.tokenInFlight) { return this.tokenInFlight; }

		this.tokenInFlight = (async () => {
			try {
				const resp = await fetch(this.aiAuthUrl, { method: 'POST', credentials: 'include' });
				if (resp.status === 401 || resp.status === 403) { throw new PmChatAuthError(); }
				if (!resp.ok) { throw new Error(`AI login failed: ${resp.status} ${resp.statusText}`); }
				const data = await resp.json() as { token: string; expiresAt?: string };
				this.token          = data.token;
				this.tokenExpiresAt = data.expiresAt ? Date.parse(data.expiresAt) : Date.now() + 10 * 60_000;
				return this.token;
			} finally {
				this.tokenInFlight = null;
			}
		})();
		return this.tokenInFlight;
	}

	/** Bearer header for MCP calls, minting/refreshing the AI JWT as needed. */
	private async authHeader(force = false): Promise<Record<string, string>> {
		const token = await this.ensureToken(force);
		return { Authorization: `Bearer ${token}` };
	}

	private clearToken(): void { this.token = null; this.tokenExpiresAt = 0; }

	/**
	 * Whether the signed-in user holds a ColdFusion security resource, read from the AI JWT's
	 * `resources` claim (space-separated resource codes, stamped at mint from CF's `hasAccess(...)`).
	 *
	 * UI-GATING ONLY. This decides whether an affordance is worth showing — it is NOT the security
	 * boundary. MCP re-checks the same claim server-side on every admin-only endpoint, so a user who
	 * forges a client-side `true` still gets a 403. Deliberately FAIL-CLOSED: a missing/!unparsable
	 * token or claim yields false, so an admin-only control stays hidden until the claim really
	 * arrives (i.e. it is hidden from everyone until ColdFusion emits `resources`).
	 */
	async hasResource(code: string): Promise<boolean> {
		try {
			const token = await this.ensureToken();
			return PmChatService.resourcesOf(token).includes(code);
		} catch {
			return false;   // not signed in / mint failed → assume no entitlement
		}
	}

	/**
	 * `Admin.Advanced` — the CF System-Admin resource that gates the advanced debug view (the full
	 * system prompt, every tool schema, and the model configuration).
	 */
	canUseAdvancedDebug(): Promise<boolean> { return this.hasResource('Admin.Advanced'); }

	/** Resource codes carried by a JWT payload's `resources` claim; [] when absent/malformed. */
	private static resourcesOf(token: string): string[] {
		const part = (token || '').split('.')[1];
		if (!part) { return []; }
		try {
			// base64url → base64, then decode the payload (no signature check: the server validates it;
			// this only reads a claim the server already vouched for when it minted the token).
			const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
			const claim = (JSON.parse(json) as { resources?: unknown }).resources;
			if (typeof claim === 'string') { return claim.split(' ').filter(Boolean); }
			return Array.isArray(claim) ? claim.filter(c => typeof c === 'string') as string[] : [];
		} catch {
			return [];
		}
	}

	/**
	 * Streams the assistant reply for `message` given the prior `history`. Completes after a
	 * `message.done` or `error` event; aborts the request on unsubscribe.
	 *
	 * @param context Live app context (page/filters/report) — sent verbatim as `ChatRequest.Context`
	 *                so the assistant can default its scope to what's on screen. Omit for none.
	 */
	stream(
		message: string,
		history: CsChatMessage[],
		sessionStartUtc?: string | null,
		context?: string | null
	): Observable<PmChatStreamEvent> {
		return new Observable<PmChatStreamEvent>(subscriber => {
			const controller = new AbortController();

			const body = JSON.stringify({
				message,
				messages: history
					.filter(m => m.role === 'user' || m.role === 'assistant')
					.map(m => ({ role: m.role, content: m.role === 'assistant' ? stripUiFences(m.content) : m.content })),
				sessionStartUtc: sessionStartUtc ?? null,
				context: context || null
			});

			// Open the stream with a Bearer token; on a 401 (token expired/rejected) re-mint once and retry.
			const open = async (force: boolean): Promise<Response> => {
				const headers = { 'Content-Type': 'application/json', ...(await this.authHeader(force)) };
				return fetch(`${this.apiUrl}/stream`, { method: 'POST', headers, body, signal: controller.signal });
			};
			return this.consume(open, controller, subscriber);
		});
	}

	/**
	 * Reattaches to a turn that is STILL GENERATING for this session: the server replays what it has
	 * already emitted, then streams the rest live. Completes immediately (no events) when nothing is
	 * running — the server answers 204.
	 *
	 * Needed because a turn outlives its request: reloading the page mid-answer leaves the server
	 * happily finishing work the user is waiting for, and without this they'd stare at a transcript
	 * frozen at the last checkpoint with no idea when to refresh.
	 */
	attach(sessionStartUtc: string): Observable<PmChatStreamEvent> {
		return new Observable<PmChatStreamEvent>(subscriber => {
			const controller = new AbortController();
			const open = async (force: boolean): Promise<Response> => {
				const headers = { ...(await this.authHeader(force)) };
				return fetch(`${this.apiUrl}/stream/${encodeURIComponent(sessionStartUtc)}`,
				             { method: 'GET', headers, signal: controller.signal });
			};
			return this.consume(open, controller, subscriber, { emptyOn204: true });
		});
	}

	/**
	 * Shared SSE pump for {@link stream} and {@link attach}: opens (re-minting the token once on 401),
	 * then parses `data:` frames into events until message.done/error or the body ends.
	 */
	private consume(
		open: (force: boolean) => Promise<Response>,
		controller: AbortController,
		subscriber: { next: (e: PmChatStreamEvent) => void; error: (e: unknown) => void; complete: () => void },
		opts: { emptyOn204?: boolean } = {}
	): () => void {

			(async () => {
				try {
					let resp = await open(false);
					if (resp.status === 401) { this.clearToken(); resp = await open(true); }
					if (resp.status === 401) { subscriber.error(new PmChatAuthError()); return; }
					// attach only: 204 = nothing generating, so there is simply nothing to replay.
					if (opts.emptyOn204 && resp.status === 204) { subscriber.complete(); return; }
					if (!resp.ok || !resp.body) {
						subscriber.error(new Error(`Chat backend returned ${resp.status} ${resp.statusText}`));
						return;
					}

					const reader  = resp.body.getReader();
					const decoder = new TextDecoder();
					let buffer = '';

					// eslint-disable-next-line no-constant-condition
					while (true) {
						const { done, value } = await reader.read();
						if (done) { break; }

						buffer += decoder.decode(value, { stream: true });

						let nl: number;
						while ((nl = buffer.indexOf('\n')) >= 0) {
							const line = buffer.slice(0, nl).trim();
							buffer = buffer.slice(nl + 1);
							if (!line.startsWith('data:')) { continue; }

							let evt: PmChatStreamEvent | undefined;
							try { evt = JSON.parse(line.slice('data:'.length).trim()); }
							catch { continue; }

							if (!evt) { continue; }
							if (!evt.type && typeof evt.value === 'string') { evt.type = 'error'; }
							subscriber.next(evt);

							if (evt.type === 'message.done' || evt.type === 'error') {
								subscriber.complete();
								return;
							}
						}
					}

					subscriber.complete();
				} catch (err) {
					if (!controller.signal.aborted) { subscriber.error(err); }
				}
		})();

		return () => controller.abort();
	}

	/** GET/DELETE/PUT helper against the MCP base with the Bearer token, re-minting once on 401. */
	private async authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
		const run = async (force: boolean) =>
			fetch(`${this.apiUrl}${path}`, { ...init, headers: { ...(init.headers ?? {}), ...(await this.authHeader(force)) } });
		let resp = await run(false);
		if (resp.status === 401) { this.clearToken(); resp = await run(true); }
		return resp;
	}

	/** Lists the current account's sessions, newest first. */
	async listSessions(): Promise<PmChatSessionSummary[]> {
		const resp = await this.authedFetch('/sessions');
		if (resp.status === 401) { throw new PmChatAuthError(); }
		if (!resp.ok) { throw new Error(`Sessions request failed: ${resp.status} ${resp.statusText}`); }
		return await resp.json() as PmChatSessionSummary[];
	}

	/**
	 * Asks the server to stop generating this session's in-flight turn.
	 *
	 * Aborting the fetch is NOT enough: a turn deliberately outlives its request so that closing the
	 * tab still finishes and saves the answer, which means a dropped connection can't imply "stop" —
	 * the two are identical at the transport layer. Without this call, Stop would abort the stream
	 * locally while the model kept running and the finished answer appeared on reopen.
	 *
	 * Fire-and-forget: Stop must feel instant, and a failed stop is not worth a message — the worst
	 * case is the turn completing as it would have anyway.
	 */
	async stopTurn(startUtc: string): Promise<void> {
		try {
			await this.authedFetch(`/stop/${encodeURIComponent(startUtc)}`, { method: 'POST' });
		} catch { /* best-effort */ }
	}

	/** Full-prompt breakdown for the advanced debug panel (GET /debug/context). Gated server-side. */
	async getDebugContext(): Promise<PmDebugContext> {
		const resp = await this.authedFetch('/debug/context');
		if (resp.status === 401) { throw new PmChatAuthError(); }
		if (!resp.ok) { throw new Error(`Debug context failed: ${resp.status} ${resp.statusText}`); }
		return await resp.json() as PmDebugContext;
	}

	/** Loads a session's turns (Prompt/Response already decompressed server-side). */
	async loadSession(startUtc: string): Promise<PmChatTurnRecord[]> {
		const resp = await this.authedFetch(`/sessions/${encodeURIComponent(startUtc)}`);
		if (resp.status === 401) { throw new PmChatAuthError(); }
		if (!resp.ok) { throw new Error(`Session load failed: ${resp.status} ${resp.statusText}`); }
		return await resp.json() as PmChatTurnRecord[];
	}

	/** Archives a session (hidden from the list, kept in the archive drawer). */
	async archiveSession(startUtc: string): Promise<void> {
		const resp = await this.authedFetch(`/sessions/${encodeURIComponent(startUtc)}`, { method: 'DELETE' });
		if (!resp.ok) { throw new Error(`Archive failed: ${resp.status} ${resp.statusText}`); }
	}

	/** Manually renames a session; returns the applied title. */
	async renameSession(startUtc: string, title: string): Promise<string> {
		const resp = await this.authedFetch(`/sessions/${encodeURIComponent(startUtc)}/title`, {
			method:  'PUT',
			headers: { 'Content-Type': 'application/json' },
			body:    JSON.stringify({ title })
		});
		if (!resp.ok) { throw new Error(`Rename failed: ${resp.status} ${resp.statusText}`); }
		const data = await resp.json().catch(() => ({} as { title?: string }));
		return data.title ?? title;
	}
}
